import React, {useCallback, useContext, useState} from 'react';
import {isMobile} from 'react-device-detect';
import {Avatar, Box, Button, Menu, Stack, Typography} from '@mui/material';
import {Link} from 'react-router';
import {AuthContext, PageContext} from '@lb/mlib-components';

// A local replacement for mlib's Header component: mlib's `logo` prop only accepts an
// image URL string with no slot between the logo and the title, so there's no way to
// place a search box next to the logo at our pinned mlib-components version. This
// mirrors mlib's Header structure exactly (styling, avatar, settings menu) but adds a
// `logoActions` slot right after the logo, matching best-ui's layout on a newer mlib
// version we can't take yet (it would break the CRA build - see package.json comments).
function getInitials(name, maxLength = 3) {
    const chars = Array.from(name.trim());
    if (name.length <= maxLength) return name;

    const initials = [];
    for (let idx = 0; idx < chars.length; idx++) {
        const char = chars[idx];
        if (char.toLowerCase() !== char || idx === 0 || /\s/.test(chars[idx - 1])) {
            initials.push(char);
            if (initials.length === maxLength) break;
        }
    }
    return initials.join('');
}

export default function AppHeader({logo, logoAlt, logoActions, barActions, settingsActions}) {
    const authCtx = useContext(AuthContext);
    const pageCtx = useContext(PageContext);
    const userName = authCtx.userName || '';
    const [anchorEl, setAnchorEl] = useState(null);

    const handleMenu = useCallback((event) => setAnchorEl(event.currentTarget), []);
    const handleClose = useCallback(() => setAnchorEl(null), []);
    const open = Boolean(anchorEl);

    return (
        <Box component="div" sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexGrow: 1,
            whiteSpace: 'nowrap'
        }}>
            <Stack direction="row" spacing={2} sx={{alignItems: 'center', flexShrink: 0}}>
                <Link to='/' style={{display: 'flex', alignItems: 'center'}}>
                    <img src={logo} alt={logoAlt} style={{display: 'block', width: isMobile ? '6rem' : '10rem'}}/>
                </Link>
                {logoActions}
            </Stack>

            <Stack direction="row" spacing={1} sx={{flexGrow: 1, justifyContent: 'center', alignItems: 'center', mx: 2}}>
                <Typography variant={!isMobile ? "h1" : 'h3'} sx={{my: 0}}>
                    {pageCtx.title}
                </Typography>
                {pageCtx.title && barActions}
            </Stack>

            <Box component="div">
                <Button
                    aria-controls="menu-appbar"
                    aria-haspopup="true"
                    onClick={settingsActions ? handleMenu : undefined}
                    style={{flexShrink: 0}}
                >
                    <Avatar sx={{width: 30, height: 30}}>
                        <Typography variant="caption">{getInitials(userName)}</Typography>
                    </Avatar>
                    <Typography variant="subtitle1" sx={{
                        color: "#FFFFFF",
                        pl: "1rem",
                        display: {xs: "none", sm: "block"},
                    }}>
                        {userName}
                    </Typography>
                </Button>
                {settingsActions &&
                    <Menu id="menu-appbar" anchorEl={anchorEl} keepMounted open={open} onClose={handleClose}>
                        {settingsActions}
                    </Menu>}
            </Box>
        </Box>
    );
}
